import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from './lib/supabaseClient'

function InvitationAccept() {
  const navigate = useNavigate()
  const { invitationId } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [invitation, setInvitation] = useState(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const [debugInfo, setDebugInfo] = useState(null)

  useEffect(() => {
    const verifyInvitation = async () => {
      console.log('=== INVITATION ACCEPT DEBUG ===')
      console.log('Full URL:', window.location.href)
      console.log('Invitation ID from params:', invitationId)
      
      if (!invitationId) {
        setError('Invalid invitation link - No ID found')
        setLoading(false)
        return
      }

      // ✅ FIX: Clean and validate UUID
      let cleanId = invitationId.trim()
      
      // ✅ FIX: Try to extract valid UUID from malformed string
      // This handles cases like "55bb69b8c-af0f-48ac-b8d0-e149d0a5ded2"
      const uuidMatch = cleanId.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)
      if (uuidMatch) {
        cleanId = uuidMatch[0]
        console.log('🔧 Extracted valid UUID:', cleanId)
      }

      // ✅ FIX: Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      if (!uuidRegex.test(cleanId)) {
        console.error('❌ Invalid UUID format:', cleanId)
        setError('Invalid invitation link format. Please contact the person who invited you.')
        setLoading(false)
        return
      }

      try {
        console.log('🔍 Checking invitation with ID:', cleanId)
        
        const { data, error } = await supabase
          .from('staff_invitations')
          .select('*')
          .eq('id', cleanId)
          .maybeSingle()

        console.log('📥 Supabase response:', { data, error })

        if (error) {
          console.error('Error fetching invitation:', error)
          setError(`Database error: ${error.message}`)
          setLoading(false)
          return
        }

        if (!data) {
          console.error('❌ No invitation found with ID:', cleanId)
          
          const { data: allInvitations } = await supabase
            .from('staff_invitations')
            .select('id, email, status, company_id')
            .limit(5)
          
          console.log('📋 Recent invitations:', allInvitations)
          setDebugInfo(allInvitations)
          
          setError(`Invitation not found. Please contact the person who invited you.`)
          setLoading(false)
          return
        }

        if (data.status !== 'pending') {
          setError(`This invitation has been ${data.status}`)
          setLoading(false)
          return
        }

        if (new Date(data.expires_at) < new Date()) {
          setError('This invitation has expired. Please request a new one.')
          setLoading(false)
          return
        }

        setInvitation(data)
        setEmail(data.email)
        setLoading(false)

      } catch (err) {
        console.error('Unexpected error:', err)
        setError('An unexpected error occurred. Please try again.')
        setLoading(false)
      }
    }

    verifyInvitation()
  }, [invitationId])

  const getPortalPath = (role) => {
    const portalMap = {
      'leadership': '/leadership',
      'agent': '/agent',
      'accounting': '/accounting',
      'marketing': '/marketing',
      'legal': '/legal',
      'recruitment': '/recruitment',
      'cro': '/cro'
    }
    return portalMap[role] || '/agent'
  }

  const handleAccept = async (e) => {
    e.preventDefault()
    
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }
    
    setAccepting(true)
    setError('')

    try {
      console.log('📤 Creating account for:', email)
      console.log('📤 Role:', invitation.role)
      console.log('📤 Company ID:', invitation.company_id)

      const { data: existingUser, error: userError } = await supabase
        .from('users')
        .select('id, email, role, company_id')
        .eq('email', email)
        .maybeSingle()

      if (existingUser) {
        console.log('👤 User already exists:', existingUser)
        
        const { error: updateError } = await supabase
          .from('users')
          .update({
            role: invitation.role,
            department: invitation.department,
            invited_by: invitation.invited_by,
            company_id: invitation.company_id,
            onboarding_completed: true,
            updated_at: new Date()
          })
          .eq('id', existingUser.id)

        if (updateError) {
          console.error('Error updating user:', updateError)
          setError('Failed to update your account. Please contact support.')
          setAccepting(false)
          return
        }

        await supabase
          .from('staff_invitations')
          .update({ status: 'accepted' })
          .eq('id', invitationId)

        alert('Account updated! Please log in with your credentials.')
        navigate('/login')
        return
      }

      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: email,
        password: password,
        options: {
          data: {
            full_name: email.split('@')[0],
            role: invitation.role,
            department: invitation.department,
            invited_by: invitation.invited_by,
            company_id: invitation.company_id,
            onboarding_completed: true
          }
        }
      })

      if (signUpError) {
        console.error('Sign up error:', signUpError)
        
        if (signUpError.message.includes('already registered')) {
          alert('An account with this email already exists. Please log in.')
          navigate('/login')
          return
        }
        
        setError(signUpError.message)
        setAccepting(false)
        return
      }

      if (authData?.user) {
        const userId = authData.user.id
        console.log('✅ User created with ID:', userId)

        const { error: insertError } = await supabase
          .from('users')
          .insert({
            id: userId,
            email: email,
            role: invitation.role,
            department: invitation.department,
            invited_by: invitation.invited_by,
            company_id: invitation.company_id,
            onboarding_completed: true,
            created_at: new Date(),
            updated_at: new Date()
          })

        if (insertError) {
          console.error('Error inserting user:', insertError)
          setError('Account created but profile setup incomplete. Please try logging in.')
          setAccepting(false)
          return
        }

        await supabase
          .from('staff_invitations')
          .update({ status: 'accepted' })
          .eq('id', invitationId)

        const portalPath = getPortalPath(invitation.role)
        alert(`Account created successfully! Redirecting to ${invitation.role} portal.`)
        navigate(portalPath)
      }

    } catch (err) {
      console.error('Error accepting invitation:', err)
      setError(err.message || 'Failed to accept invitation')
      setAccepting(false)
    } finally {
      setAccepting(false)
    }
  }

  if (debugInfo && !loading && error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ ...styles.icon, color: '#f59e0b' }}>🔍</div>
          <h1 style={{ ...styles.title, color: '#f59e0b' }}>Debug Information</h1>
          <p style={styles.text}>Recent invitations in the database:</p>
          <pre style={{ 
            background: '#f8fafc', 
            padding: '16px', 
            borderRadius: '8px', 
            fontSize: '12px', 
            textAlign: 'left', 
            overflow: 'auto' 
          }}>
            {JSON.stringify(debugInfo, null, 2)}
          </pre>
          <p style={styles.text}>Your invitation ID: <strong>{invitationId}</strong></p>
          <button onClick={() => navigate('/login')} style={styles.button}>
            Go to Login
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.icon}>⏳</div>
          <h1 style={styles.title}>Verifying Invitation...</h1>
          <p style={styles.text}>Please wait while we verify your invitation.</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ ...styles.icon, color: '#dc2626' }}>❌</div>
          <h1 style={{ ...styles.title, color: '#dc2626' }}>Invitation Error</h1>
          <p style={styles.text}>{error}</p>
          <button onClick={() => navigate('/login')} style={styles.button}>
            Go to Login
          </button>
          <br />
          <button 
            onClick={() => window.location.reload()} 
            style={{ ...styles.button, background: '#64748b', marginTop: '10px' }}
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.icon}>📧</div>
        <h1 style={styles.title}>Accept Invitation</h1>
        <p style={styles.text}>
          You've been invited to join as a <strong>{invitation?.role}</strong>.
        </p>

        <div style={styles.infoBox}>
          <p><strong>📧 Email:</strong> {invitation?.email}</p>
          <p><strong>👔 Role:</strong> {invitation?.role}</p>
          <p><strong>📂 Department:</strong> {invitation?.department || 'Not specified'}</p>
          <p><strong>⏰ Expires:</strong> {new Date(invitation?.expires_at).toLocaleDateString()}</p>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleAccept}>
          <div style={styles.passwordContainer}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Create a password (min 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={styles.passwordToggle}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>

          <input
            type="password"
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={styles.input}
            required
          />

          <button type="submit" disabled={accepting} style={styles.button}>
            {accepting ? 'Creating Account...' : 'Accept Invitation →'}
          </button>
        </form>

        <button onClick={() => navigate('/login')} style={styles.linkBtn}>
          Already have an account? Log in
        </button>
      </div>
    </div>
  )
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2b42 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: "'Inter', system-ui, sans-serif"
  },
  card: {
    background: '#fff',
    borderRadius: '24px',
    padding: '40px',
    width: '100%',
    maxWidth: '450px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    position: 'relative',
    textAlign: 'center'
  },
  icon: { fontSize: '48px', marginBottom: '16px' },
  title: { fontSize: '28px', fontWeight: '700', color: '#1e3a5f', marginBottom: '8px' },
  text: { fontSize: '16px', color: '#64748b', marginBottom: '20px', lineHeight: '1.6' },
  infoBox: {
    background: '#f8fafc',
    padding: '16px',
    borderRadius: '12px',
    marginBottom: '20px',
    textAlign: 'left',
    fontSize: '14px',
    lineHeight: '1.8'
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    margin: '8px 0',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '16px',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  passwordContainer: {
    position: 'relative',
    width: '100%',
    margin: '8px 0'
  },
  passwordToggle: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#64748b',
    padding: '4px 8px',
    borderRadius: '6px'
  },
  button: {
    width: '100%',
    padding: '14px',
    marginTop: '16px',
    background: '#1e3a5f',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  error: {
    background: '#fee2e2',
    color: '#dc2626',
    padding: '12px',
    borderRadius: '12px',
    marginBottom: '16px',
    fontSize: '14px'
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: '#2c7da0',
    fontSize: '14px',
    cursor: 'pointer',
    marginTop: '16px',
    width: '100%'
  }
}

export default InvitationAccept